/**
 * Admin feature registration.
 *
 * Every page — including `AdminLayout` and the two pages owned by other
 * features — is behind `lazy()`. Previously all 22 were static imports, which
 * fused three features' page graphs into a single ~187 KB chunk that was then
 * awaited before first paint. See `features/registry.tsx` for the contract.
 */
import React, { lazy } from 'react'
import { Navigate, Route } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { lazyRoute } from '../routeHelpers'
import type { FrontendFeature } from '../types'

const AdminLayout = lazy(() =>
  import('./components/AdminLayout').then((m) => ({ default: m.AdminLayout })),
)
const AdminDashboardPage = lazy(() =>
  import('./pages/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
)
const AdminUsersPage = lazy(() =>
  import('./pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminUserDetailPage = lazy(() =>
  import('./pages/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage })),
)
const AdminRolesPage = lazy(() =>
  import('./pages/AdminRolesPage').then((m) => ({ default: m.AdminRolesPage })),
)
const SystemInfoPage = lazy(() =>
  import('./pages/SystemInfoPage').then((m) => ({ default: m.SystemInfoPage })),
)
const AdminSettingsPage = lazy(() =>
  import('./pages/AdminSettingsPage').then((m) => ({ default: m.AdminSettingsPage })),
)
const AdminEntitlementsPage = lazy(() =>
  import('./pages/AdminEntitlementsPage').then((m) => ({ default: m.AdminEntitlementsPage })),
)
const AdminEntitlementDetailPage = lazy(() =>
  import('./pages/AdminEntitlementDetailPage').then((m) => ({
    default: m.AdminEntitlementDetailPage,
  })),
)
const AdminM2MClientsPage = lazy(() =>
  import('./pages/AdminM2MClientsPage').then((m) => ({ default: m.AdminM2MClientsPage })),
)
const AdminJobsPage = lazy(() =>
  import('./pages/AdminJobsPage').then((m) => ({ default: m.AdminJobsPage })),
)
const TenantProfilePage = lazy(() =>
  import('./pages/TenantProfilePage').then((m) => ({ default: m.TenantProfilePage })),
)
const PermissionsAdminPage = lazy(() =>
  import('./pages/PermissionsAdminPage').then((m) => ({ default: m.PermissionsAdminPage })),
)
const MediaLibraryPage = lazy(() =>
  import('./pages/MediaLibraryPage').then((m) => ({ default: m.MediaLibraryPage })),
)
const NotificationTemplatesPage = lazy(() =>
  import('./pages/NotificationTemplatesPage').then((m) => ({
    default: m.NotificationTemplatesPage,
  })),
)
const AuditEventsPage = lazy(() =>
  import('./pages/AuditEventsPage').then((m) => ({ default: m.AuditEventsPage })),
)
const AdminSubscriptionPage = lazy(() =>
  import('./pages/AdminSubscriptionPage').then((m) => ({ default: m.AdminSubscriptionPage })),
)
const AdminSistersPage = lazy(() =>
  import('./pages/AdminSistersPage').then((m) => ({ default: m.AdminSistersPage })),
)
const AdminCurrenciesPage = lazy(() =>
  import('./pages/AdminCurrenciesPage').then((m) => ({ default: m.AdminCurrenciesPage })),
)
const AdminVendorsPage = lazy(() =>
  import('./pages/AdminVendorsPage').then((m) => ({ default: m.AdminVendorsPage })),
)
const AdminManufacturersPage = lazy(() =>
  import('./pages/AdminManufacturersPage').then((m) => ({ default: m.AdminManufacturersPage })),
)
// Owned by other features, but `/admin/*` belongs to admin so admin declares
// the route. Lazy, so the edge costs a chunk boundary rather than a graph edge.
const AdminProjectsRecycleBinPage = lazy(() =>
  import('@/features/projects/pages/AdminProjectsRecycleBinPage').then((m) => ({
    default: m.AdminProjectsRecycleBinPage,
  })),
)
const AdminReportTemplatesPage = lazy(() =>
  import('@/features/projects/operations/pages/AdminReportTemplatesPage').then((m) => ({
    default: m.AdminReportTemplatesPage,
  })),
)

const feature: FrontendFeature = {
  navItems: [
    { path: '/admin', label: 'Admin', icon: Shield, requiredPermissions: ['admin:*'] },
  ],
  routes: [
    <Route
      key="admin"
      path="/admin"
      element={
        <ProtectedRoute requiredPermissions={['admin:*']}>
          {lazyRoute(<AdminLayout />)}
        </ProtectedRoute>
      }
    >
      {/* Phase 1: /admin lands on Users. AdminDashboardPage route kept for re-enablement. */}
      <Route index element={<Navigate to="/admin/users" replace />} />
      <Route path="dashboard" element={lazyRoute(<AdminDashboardPage />)} />
      <Route path="users" element={lazyRoute(<AdminUsersPage />)} />
      <Route path="users/:id" element={lazyRoute(<AdminUserDetailPage />)} />
      <Route path="roles" element={lazyRoute(<AdminRolesPage />)} />
      <Route path="entitlements" element={lazyRoute(<AdminEntitlementsPage />)} />
      <Route path="entitlements/:id" element={lazyRoute(<AdminEntitlementDetailPage />)} />
      <Route path="m2m-clients" element={lazyRoute(<AdminM2MClientsPage />)} />
      <Route path="jobs" element={lazyRoute(<AdminJobsPage />)} />
      <Route path="settings" element={lazyRoute(<AdminSettingsPage />)} />

      {/* Stage 1 admin pages */}
      <Route path="tenant" element={lazyRoute(<TenantProfilePage />)} />
      <Route path="permissions" element={lazyRoute(<PermissionsAdminPage />)} />
      <Route path="audit-events" element={lazyRoute(<AuditEventsPage />)} />
      <Route path="notification-templates" element={lazyRoute(<NotificationTemplatesPage />)} />
      <Route path="media" element={lazyRoute(<MediaLibraryPage />)} />

      {/* Stage 2 admin pages */}
      <Route path="subscription" element={lazyRoute(<AdminSubscriptionPage />)} />
      <Route path="sisters" element={lazyRoute(<AdminSistersPage />)} />

      {/* Stage 3 admin pages */}
      <Route path="currencies" element={lazyRoute(<AdminCurrenciesPage />)} />
      <Route path="vendors" element={lazyRoute(<AdminVendorsPage />)} />
      <Route path="manufacturers" element={lazyRoute(<AdminManufacturersPage />)} />

      {/* Stage 7 admin pages */}
      <Route path="projects/recycle-bin" element={lazyRoute(<AdminProjectsRecycleBinPage />)} />

      {/* Stage 8 admin pages */}
      <Route path="report-templates" element={lazyRoute(<AdminReportTemplatesPage />)} />

      {/* HIDDEN — the old starter dashboard, kept as environment/session diagnostics when
          the Dealer Dashboard took over `/dashboard` (task06). Intentionally absent from
          the admin sidebar and unlinked from anywhere; direct URL only. Adding a nav entry
          would put a debugging page in front of every admin. */}
      <Route path="system-info" element={lazyRoute(<SystemInfoPage />)} />
    </Route>,
  ],
}

export default feature
